import { icon } from "@fortawesome/fontawesome-svg-core";
import { faVideoPlus } from "@fortawesome/pro-solid-svg-icons/faVideoPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor } from "@tinymce/tinymce-react";
import nl2br from "nl2br";
import pluralize from "pluralize";
import { PropTypes } from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SegmentedMessage } from "sms-segments-calculator";
import Tooltipable from "~/components/effects/tooltipable";
import GlobalContainer from "~/components/global_container";
import AssistModal from "~/components/modals/AiAssist/Modal";
import MmsAttachmentModal from "~/components/modals/attachments/MmsAttachmentModal";
import { NOOP_FALSE } from "~/constants/callbacks";
import {
  SMS_TEXT_EDITOR_PLUGINS,
  SMS_TEXT_EDITOR_TOOLBAR,
  TINYMCE_SCRIPT_PATH,
  TINYMCE_SCROLLABLE_UI_OPTIONS,
} from "~/constants/TinyMCE";
import EditorHelpers from "~/helpers/editor_helpers";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { renderComponent } from "~/helpers/reactRoot";
import {
  isValidDocumentURL,
  isValidVideoURL,
} from "~/helpers/SmsMessages/contentURLChecks";
import { initWebSpellChecker } from "~/lib/web_spell_checker";

const tooltipText = (
  <>
    Please Note on Text Segment Usage:
    <ul className="pl-4">
      <li>
        Text messages are limitied to 160 characters and messages &gt; 160
        characters will be split across multiple segments. Certain characters
        like emojis 😁 can increase your count faster.
      </li>
      <li>MMS segments with image or videos count as 3 segments.</li>
      <li>
        Please be sure to monitor your text messaged segment usage{" "}
        <a
          href="/billing"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white"
        >
          here
        </a>
        .
      </li>
    </ul>
  </>
);

const SmsTextEditor = ({
  id = "",
  value = "",
  mms = false,
  className = "",
  placeholder = "Enter text or insert ...",
  replaceValue = null,
  clearReplaceValue = NOOP_FALSE,
  leadId = null,
  linksToInsert = [],
  clearLinksToInsert = NOOP_FALSE,
  onChange = NOOP_FALSE,
  handleMediaUrlChange = NOOP_FALSE,
  charsLimit = null,
}) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [charsCount, setCharsCount] = useState(0);
  const [segments, setSegments] = useState(0);
  const [hasURL, setHasURL] = useState(false);
  const [showVideoWarning, setShowVideoWarning] = useState(false);
  const [showAssistModal, setShowAssistModal] = useState(false);
  const editorRef = useRef(null);

  const displayText = useMemo(
    () =>
      charsLimit
        ? `${charsCount} / ${charsLimit} characters`
        : pluralize("Characters", charsCount, true),
    [charsCount, charsLimit],
  );

  useEffect(() => {
    if (replaceValue) {
      const text = formatContent(replaceValue);
      setContent(text);
      clearReplaceValue();
    }
  }, [replaceValue, clearReplaceValue]);

  useEffect(() => {
    if (linksToInsert?.length) {
      let newContent = content;
      linksToInsert.forEach((link) => {
        const { href } = link;
        const linkHtml = `<br/><a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>&nbsp;`;

        newContent += linkHtml;
      });
      setContent(newContent);
      clearLinksToInsert();
    }
  }, [linksToInsert, clearLinksToInsert]);

  const formatContent = (text) => nl2br(text);

  const countTextChars = (text) => {
    let count = 0;
    if (typeof text === "string") {
      count = new SegmentedMessage(text).numberOfCharacters;
    }
    return count;
  };

  const countTextSegments = (text) => {
    let newSegments = 0;
    if (typeof text === "string") {
      const textWithoutNonBreakingSpaces = text.replace(/\u00A0/g, " ");
      newSegments = new SegmentedMessage(textWithoutNonBreakingSpaces)
        .segmentsCount;
    }
    return newSegments;
  };

  const handleEditorChange = (newContent, editor) => {
    let text = "";
    text = editor.getContent({ format: "text" });
    const urlPattern = /https?:\/\/[^\s]+/g;
    const urls = text.match(urlPattern) || [];

    const hasValidURL = urls.some(
      (url) => isValidVideoURL(url) || isValidDocumentURL(url),
    );
    const newSegmentsCount = countTextSegments(text);

    setContent(newContent);
    setSegments(newSegmentsCount);
    setCharsCount(countTextChars(text));
    setHasURL(hasValidURL);
    onChange(text, newSegmentsCount);
  };

  const handleOnInit = (_event, editor) => {
    editorRef.current = editor;
    setLoading(false);

    if (value) {
      const text = formatContent(value);
      editor.setContent(text, { format: "text" });
    }

    initWebSpellChecker(editor.iframeElement);
  };

  return (
    <>
      {showVideoWarning && (
        <div className="alert alert-info my-2">
          It looks like you&apos;re trying to add a video to your text — great
          choice! Please{" "}
          <a
            href="/billing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary"
          >
            upgrade your pricing plan
          </a>{" "}
          to enable this feature.
        </div>
      )}

      <label htmlFor="sms-text-body" className="d-block">
        Body
      </label>

      <div className={`${className} position-relative`}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center position-absolute w-100 h-100 bg-white z-5">
            <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />{" "}
            Loading
          </div>
        )}

        <Editor
          id={id}
          tinymceScriptSrc={TINYMCE_SCRIPT_PATH}
          scriptLoading={{ async: true }}
          value={content}
          onEditorChange={handleEditorChange}
          onInit={handleOnInit}
          init={{
            placeholder,
            cache_suffix: "?v=7.9.1",
            license_key: "gpl",
            ...TINYMCE_SCROLLABLE_UI_OPTIONS,
            plugins: SMS_TEXT_EDITOR_PLUGINS,
            toolbar: SMS_TEXT_EDITOR_TOOLBAR,
            toolbar_mode: "wrap",
            branding: false,
            menubar: false,
            min_height: 350,
            relative_urls: false,
            remove_script_host: false,
            document_base_url: Rails.baseUrl,
            entity_encoding: "raw",
            content_style: "body { font-family: Arial; font-size: 13px; }",
            paste_as_text: true,
            contextmenu: false,
            paste_remove_styles_if_webkit: false,
            statusbar: false,
            newline_behavior: "linebreak",
            forced_root_block: "div",
            setup: (editor) => {
              editor.ui.registry.addIcon(
                "videoIcon",
                icon(faVideoPlus).html[0],
              );
              editor.ui.registry.addButton("images", {
                icon: "image",
                tooltip: "Insert Image",
                onAction: () => {
                  renderComponent(
                    <MmsAttachmentModal
                      containerID="secondary-modal"
                      modalClass="modal modal-overlay"
                      dialogClass="modal-dialog"
                      handleMediaUrlChange={handleMediaUrlChange}
                    />,
                    document.getElementById("secondary-modal"),
                  );
                },
              });

              editor.ui.registry.addButton("videos", {
                icon: "videoIcon",
                tooltip: "Insert Video",
                onAction: async () => {
                  const isVideoTextingFeatureEnabled = checkFeatureFlag(
                    process.env.SHAKAPACKER_PUBLIC_VIDEO_TEXT_FEATURE_FLAG,
                  );

                  if (isVideoTextingFeatureEnabled) {
                    const { mountVideosModal } = await import(
                      /* webpackChunkName: "videos-modal" */ "~/components/modals/videos/VideosModal"
                    );
                    mountVideosModal({
                      modalClass: "modal modal-overlay",
                      dialogClass: "modal-dialog modal-lg",
                      editor,
                      sms: true,
                    });
                  } else {
                    setShowVideoWarning(true);
                  }
                },
              });

              editor.ui.registry.addButton("ai-assist", {
                icon: "ai",
                tooltip: "AI Assist",
                onAction: () => setShowAssistModal(true),
              });

              EditorHelpers.addVariablesMenu(editor, {
                includeEmailSignature: false,
              });
            },
          }}
        />
      </div>

      {!loading && (
        <div className="mt-3">
          <Tooltipable
            placement="right"
            text={tooltipText}
            tooltipContentClassName="text-left"
          >
            <span>
              {displayText} -{" "}
              {mms
                ? "3 MMS Segments"
                : pluralize("SMS Segments", segments, true)}
            </span>
          </Tooltipable>

          {hasURL && (
            <div className="alert alert-info" role="alert">
              Note: The hyperlink in this text will be shortened as it is
              delivered.
            </div>
          )}

          {segments > 1 && (
            <div className="alert alert-warning mt-2" role="alert">
              This is a multi segment message and text billing is per segment
            </div>
          )}
        </div>
      )}

      {showAssistModal && (
        <AssistModal
          show
          onHide={() => setShowAssistModal(false)}
          editor={editorRef.current}
          mode="text"
          editorType="sms"
          leadId={leadId}
          product={GlobalContainer.product()}
        />
      )}
    </>
  );
};

SmsTextEditor.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  mms: PropTypes.bool,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  replaceValue: PropTypes.string,
  linksToInsert: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ),
  clearLinksToInsert: PropTypes.func,
  handleMediaUrlChange: PropTypes.func,
  clearReplaceValue: PropTypes.func,
  leadId: PropTypes.number,
  onChange: PropTypes.func,
  charsLimit: PropTypes.number,
};

export default SmsTextEditor;
